import { trackEvent } from "@/services/analytics-service";
import { AnalyticsKey } from "@/types/analytics";
import { AuthData } from "@/types/auth";
import { ClauseData, Playbook } from "@/types/risk-analysis";
import api from "./api";
import log from "../utils/logger";

export const getPlaybooks = async (user: AuthData): Promise<Playbook[]> => {
  try {
    if (!user.user.customerId) throw new Error("Customer ID not found");

    const response = await api.get(
      `/playbook?customerId=${user.user.customerId}&isActive=true&sort[order]=asc&sort[field]=createdAt`
    );
    const playbooks = response.data?.body.playbooks || [];

    trackEvent(AnalyticsKey.PLAYBOOK.FETCH.SUCCESS, {
      userId: user.user.id,
      playbooks,
    });

    return playbooks;
  } catch (error) {
    trackEvent(AnalyticsKey.PLAYBOOK.FETCH.FAILURE, {
      userId: user.user.id,
      errorMessage: error?.message || "Error fetching NDA policies playbook",
    });

    log.error("Error fetching NDA policies playbook:", "getPlaybooks", error);
    throw error;
  }
};

export const createPlaybook = async (
  user: AuthData,
  name: string,
  isPrivate: boolean,
  isEditable: boolean,
  playbookRules: ClauseData[]
): Promise<Playbook> => {
  try {
    if (!user.user.customerId) throw new Error("Customer ID not found");

    // 1. create playbook
    const response = await api.post("/playbook", {
      name,
      editable: isEditable,
      creatorUserId: user.user.id,
      customerId: user.user.customerId,
      ownerUserId: isPrivate ? user.user.id : null,
    });
    const playbook = response.data?.body;

    if (playbookRules.length) {
      // 2. create playbook rules
      const newPlaybookRules = await Promise.all(
        playbookRules.map(async (rule, index) => ({
          original: rule,
          response: await api.post(`/playbook/${playbook.id}/playbook-rules`, {
            ...rule,
            order: index,
            isActive: true,
            playbookId: playbook.id,
          }),
        }))
      );

      // 3. create fallback rules
      await Promise.all(
        newPlaybookRules
          .filter((rule) => rule.original.customerRules.length > 0)
          .map((rule) =>
            api.post(`/playbook-rules/${rule.response.data?.body.id}/fallback-rules`, {
              rules: rule.original.customerRules,
            })
          )
      );
    }

    trackEvent(AnalyticsKey.PLAYBOOK.CREATE.SUCCESS, {
      userId: user.user.id,
      playbook,
    });

    return playbook;
  } catch (error) {
    trackEvent(AnalyticsKey.PLAYBOOK.CREATE.FAILURE, {
      userId: user.user.id,
      errorMessage: error?.message || "Error creating NDA policies playbook",
    });

    log.error("Error creating NDA policies playbook:", "createPlaybook", error);
    throw error;
  }
};

export const duplicatePlaybook = async (
  user: AuthData,
  id: string,
  name: string,
  isPrivate: boolean,
  isEditable: boolean,
  originalId?: string
): Promise<Playbook> => {
  try {
    if (!user.user.customerId) throw new Error("Customer ID not found");

    const response = await api.post(`/playbook/${id}/duplicate`, {
      name,
      editable: isEditable,
      creatorUserId: user.user.id,
      customerId: user.user.customerId,
      ownerUserId: isPrivate ? user.user.id : null,
      originalPlaybookId: originalId,
    });
    const playbook = response.data?.body;

    trackEvent(AnalyticsKey.PLAYBOOK.DUPLICATE.SUCCESS, {
      userId: user.user.id,
      playbook,
    });

    return playbook;
  } catch (error) {
    trackEvent(AnalyticsKey.PLAYBOOK.DUPLICATE.FAILURE, {
      userId: user.user.id,
      errorMessage: error?.message || "Error creating NDA policies playbook",
    });

    log.error("Error creating NDA policies playbook:", "createPlaybook", error);
    throw error;
  }
};

export const updatePlaybook = async (
  user: AuthData,
  id: string,
  name: string,
  isPrivate: boolean,
  isEditable: boolean,
  playbookRules: ClauseData[]
): Promise<Playbook> => {
  try {
    if (!user.user.customerId) throw new Error("Customer ID not found");

    // 1. update playbook
    const response = await api.put(`/playbook/${id}`, {
      name,
      editable: isEditable,
      ownerUserId: isPrivate ? user.user.id : null,
    });
    const playbook = response.data?.body;
    const existingPlaybookRules = (playbook.rules ?? []) as ClauseData[];

    // 2. update playbook rules
    const existingIds = new Set(existingPlaybookRules.map((rule) => rule.id));
    const toCreate = playbookRules.filter((rule) => !existingIds.has(rule.id));
    const toUpdate = playbookRules.filter((rule) => existingIds.has(rule.id));
    const toDelete = existingPlaybookRules.filter((rule) => !playbookRules.find((pRule) => pRule.id === rule.id));

    await Promise.all([
      Promise.all(
        toCreate.map((rule, index) =>
          api.post(`/playbook/${playbook.id}/playbook-rules`, {
            ...rule,
            order: index,
            isActive: true,
            playbookId: playbook.id,
          })
        )
      ),
      Promise.all(toUpdate.map((rule) => api.put(`/playbook-rules/${rule.id}`, rule))),
      Promise.all(toDelete.map((rule) => api.delete(`/playbook-rules/${rule.id}`))),
    ]);

    // 3. update fallback rules
    await Promise.all(
      playbookRules.map((rule) => api.put(`/playbook-rules/${rule.id}/fallback-rules`, { rules: rule.customerRules }))
    );
    trackEvent(AnalyticsKey.PLAYBOOK.UPDATE.SUCCESS, {
      userId: user.user.id,
      playbook,
    });

    return playbook;
  } catch (error) {
    trackEvent(AnalyticsKey.PLAYBOOK.UPDATE.FAILURE, {
      userId: user.user.id,
      errorMessage: error?.message || "Error creating NDA policies playbook",
    });

    log.error("Error creating NDA policies playbook:", "createPlaybook", error);
    throw error;
  }
};

export const deletePlaybook = async (user: AuthData, id: string): Promise<Playbook> => {
  try {
    if (!user.user.customerId) throw new Error("Customer ID not found");

    const response = await api.delete(`/playbook/${id}`);
    const playbook = response.data?.body;

    trackEvent(AnalyticsKey.PLAYBOOK.DELETE.SUCCESS, {
      userId: user.user.id,
      playbook,
    });

    return playbook;
  } catch (error) {
    trackEvent(AnalyticsKey.PLAYBOOK.DELETE.FAILURE, {
      userId: user.user.id,
      errorMessage: error?.message || "Error creating NDA policies playbook",
    });

    log.error("Error creating NDA policies playbook:", "createPlaybook", error);
    throw error;
  }
};
